import { inject, injectable } from 'inversify';
import {
  AddressesRepositoryInterface,
  CheckStoreAvailabilityParams,
  CreateAddressParams,
  DeleteAddressParams,
  GetAddressByLocationParams,
  GetUserAddressesParams,
  SetMainAddressParams,
} from '../domain/AddressesRepositoryInterface';
import AddressesService from 'src/features/addresses/data/service/addressesService';
import LocationService from './service/locationService';
import {
  AddressComponent,
  AddressComponentType,
} from './service/locationService.types';
import { AddressDetails, AddressName } from 'src/types/common';
import { Coordinates } from '../presentation/types';
import { EditAddressRequestParams } from 'src/features/addresses/data/service/addressesService.types';

const ADDRESS_COMPONENTS_KEYS_MAP: {
  [x in keyof Omit<
    AddressDetails,
    'geolocation' | 'formatted_address' | 'interior_number' | 'name'
  >]: AddressComponentType[];
} = {
  street_name: [
    'route',
    'establishment',
    'transit_station',
    'point_of_interest',
  ],
  city: ['administrative_area_level_2', 'locality'],
  zip_code: ['postal_code'],
  street_number: ['street_number'],
  state: ['administrative_area_level_1'],
  neighborhood: ['neighborhood', 'sublocality', 'locality', 'political'],
  extra_info: ['neighborhood'],
};

const VALID_ADDRESS_NAMES: Array<string> = [
  AddressName.Home,
  AddressName.Office,
  AddressName.Other,
];

@injectable()
class AddressesRepository implements AddressesRepositoryInterface {
  @inject(AddressesService) private addressesService!: AddressesService;
  @inject(LocationService) private locationService!: LocationService;

  async getUserAddresses(params: GetUserAddressesParams) {
    const response = await this.addressesService.getAll(params);
    const addresses = response.data.results.map((address) => ({
      ...address,
      name: this.getValidAddressName(address.name),
    }));

    return addresses;
  }

  async setMainAddress(params: SetMainAddressParams) {
    const response = await this.addressesService.setMain(params);
    const address = response.data;
    const addressName = this.getValidAddressName(address.name);

    return { ...address, name: addressName };
  }

  async createAddress(params: CreateAddressParams) {
    const addressPayload: CreateAddressParams['address'] = {
      ...params.address,
      extra_info:
        params.address.extra_info === ''
          ? undefined
          : params.address.extra_info,
      interior_number:
        params.address.interior_number === ''
          ? undefined
          : params.address.interior_number,
    };
    const response = await this.addressesService.create({
      ...params,
      address: addressPayload,
    });
    const address = response.data;

    return address;
  }

  async editAddress(params: EditAddressRequestParams) {
    const addressPayload: EditAddressRequestParams['address'] = {
      ...params.address,
      extra_info:
        params.address.extra_info === ''
          ? undefined
          : params.address.extra_info,
      interior_number:
        params.address.interior_number === ''
          ? undefined
          : params.address.interior_number,
    };
    const response = await this.addressesService.edit({
      ...params,
      address: addressPayload,
    });
    const address = response.data;

    return address;
  }

  async getAddressByLocation({
    latitude,
    longitude,
    placeId,
  }: GetAddressByLocationParams): Promise<AddressDetails> {
    const response = await this.locationService.getLocationAddress({
      lat: latitude,
      lng: longitude,
      placeId,
    });
    const address = response.data;
    const addressDetails = this.getAddressDetails({
      addressComponents: address.results[0].address_components,
      coordinates: { lat: latitude, lng: longitude },
      formattedAddress: address.results[0].formatted_address,
    });

    return addressDetails;
  }

  public getAddressDetails({
    addressComponents,
    coordinates,
    formattedAddress,
  }: {
    addressComponents: AddressComponent[];
    coordinates: Coordinates;
    formattedAddress: string;
  }): AddressDetails {
    const addressComponentsMap = addressComponents.reduce(
      (acc, curr) => {
        const key = curr.types[0];
        const isStateComponent =
          key === 'administrative_area_level_1' || key === 'political';
        const value = isStateComponent ? curr.short_name : curr.long_name;

        return { ...acc, [key]: value };
      },
      {} as { [x in AddressComponentType]: string }
    );

    const latitude = coordinates.lat as number;
    const longitude = coordinates.lng as number;

    const getAddressComponent = (keys: AddressComponentType[]) => {
      const addressComponentKey = keys.find(
        (key) => !!addressComponentsMap[key]
      );
      return addressComponentKey
        ? addressComponentsMap[addressComponentKey]
        : '';
    };

    return {
      street_name: getAddressComponent(
        ADDRESS_COMPONENTS_KEYS_MAP['street_name']
      ),
      city: getAddressComponent(ADDRESS_COMPONENTS_KEYS_MAP['city']),
      extra_info: '',
      geolocation: { coordinates: [latitude, longitude], type: '' },
      state: getAddressComponent(ADDRESS_COMPONENTS_KEYS_MAP['state']),
      street_number: getAddressComponent(
        ADDRESS_COMPONENTS_KEYS_MAP['street_number']
      ),
      zip_code: getAddressComponent(ADDRESS_COMPONENTS_KEYS_MAP['zip_code']),
      neighborhood: getAddressComponent(
        ADDRESS_COMPONENTS_KEYS_MAP['neighborhood']
      ),
      formatted_address: formattedAddress,
      interior_number: '',
      name: '',
    };
  }

  async checkStoreAvailability({
    storeSlug,
    accessToken,
  }: CheckStoreAvailabilityParams) {
    const response = await this.addressesService.checkStoreAvailability({
      accessToken,
      storeSlugName: storeSlug,
    });

    const {
      result,
      next_close: nextClose,
      next_open: nextOpen,
    } = response.data;

    const isAvailable = result === 'available';

    return { isAvailable, nextClose, nextOpen };
  }

  private getValidAddressName(name: string) {
    const validName = VALID_ADDRESS_NAMES.includes(name)
      ? name
      : AddressName.Other;

    return validName;
  }

  async deleteAddress(params: DeleteAddressParams) {
    const response = await this.addressesService.delete(params);

    return response.data;
  }
}

export default AddressesRepository;
